import {Controller} from "@hotwired/stimulus"

// Expandable detail row that loads its contents the first time it is opened.
//
// Put the controller on the container (a tbody, a table, a section) and give each
// trigger the id of the row it opens:
//
//   %button{ data: { action: 'lazy-expand#toggle',
//                    'lazy-expand-row-param': "po-detail-#{order.id}",
//                    'lazy-expand-url-param': progress_admin_..._path(order) } }
//   %tr{ id: "po-detail-#{order.id}", class: 'ice:hidden' }
//     %td{ colspan: n, data: { 'lazy-expand-target': 'body' } }
//
// Omit the url param and it is a plain toggle with no request.
//
// Why not one of the existing controllers: `replace` swaps the controller element's
// own outerHTML, `toggle_content` has no fetch, `refresh` is on a timer, and `sort`
// re-requests on every click. None of them toggle a row and remember what they
// already fetched.
export default class extends Controller {
    static targets = ["body"]

    toggle(event) {
        const trigger = event.currentTarget
        const row = document.getElementById(trigger.dataset.lazyExpandRowParam)
        if (!row) return

        const open = row.classList.toggle("ice:hidden") === false
        trigger.setAttribute("aria-expanded", open ? "true" : "false")

        const chevron = trigger.querySelector("i")
        if (chevron) {
            chevron.classList.toggle("fa-chevron-right", !open)
            chevron.classList.toggle("fa-chevron-down", open)
        }

        if (open) this.load(trigger, row)
    }

    load(trigger, row) {
        const url = trigger.dataset.lazyExpandUrlParam
        if (!url) return

        // Fetched once. Collapsing and reopening must not re-request: on a list of
        // fifty orders that turns idle clicking into a stream of queries.
        const body = this.bodyFor(row)
        if (!body || body.dataset.loaded === "true") return
        body.dataset.loaded = "true"

        body.innerHTML = this.placeholder("Loading...")

        fetch(url, {
            credentials: "same-origin",
            headers: {"X-Requested-With": "XMLHttpRequest"}
        })
            .then((response) => {
                if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)
                return response.text()
            })
            .then((html) => {
                body.innerHTML = html
            })
            .catch((error) => {
                // Clear the flag so the next open retries instead of leaving the row
                // stuck on a stale error.
                body.dataset.loaded = "false"
                body.innerHTML = this.placeholder(`Could not load this order: ${error.message}`, true)
            })
    }

    // The target lookup is scoped to the row, not the controller: one controller
    // element holds every row in the table, so `this.bodyTarget` would return the
    // first one for all of them.
    bodyFor(row) {
        return row.querySelector('[data-lazy-expand-target="body"]') || row
    }

    placeholder(message, isError = false) {
        const tone = isError ? "ice:text-danger-700" : "ice:text-slate-500"
        return `<div class="ice:px-4 ice:py-6 ice:text-center ice:text-sm ${tone}">${message}</div>`
    }
}
